// Checkpoint management service - Updated to match Postman Collection
import { Checkpoint } from '../types';
import { getCurrentConfig, getApiScope } from '../config';
import { API_ENDPOINTS, DEFAULT_HEADERS } from '../config/constants';

interface CreateCheckpointData {
  terminal_id: number;
  checkpoint_name: string;
  description?: string;
  is_open?: boolean;
  is_active?: boolean;
}

interface UpdateCheckpointData {
  terminal_id?: number;
  checkpoint_name?: string;
  description?: string;
  is_open?: boolean;
  is_active?: boolean;
}

interface ToggleCheckpointOptions {
  cascade?: boolean;
  updated_by?: string;
}

class CheckpointService {
  private getBaseURL(): string {
    return getCurrentConfig().apiBaseUrl;
  }

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const baseUrl = this.getBaseURL();
    
    // Fix double slash issue - simple URL construction
    let url = baseUrl + endpoint;
    url = url.replace(/\/+/g, '/'); // Replace multiple slashes with single slash
    url = url.replace(':/', '://'); // Fix protocol if we accidentally broke it
    
    // Get access token if available (same pattern as api.ts)
    let token: string | null = null;
    try {
      const config = getCurrentConfig();
      
      if (config.auth.enableAuthentication) {
        const { msalInstance } = await import('../contexts/AuthContext');
        const accounts = msalInstance.getAllAccounts();
        
        if (accounts.length > 0) {
          const tokenResponse = await msalInstance.acquireTokenSilent({
            scopes: [getApiScope()], // Use scope from .env
            account: accounts[0]
          });
          token = tokenResponse.accessToken;
        }
      }
    } catch (error) {
      // Silent fail for token acquisition
    }
    
    const headers: Record<string, string> = {
      ...DEFAULT_HEADERS,
    };
    
    // Add any additional headers from options
    if (options?.headers) {
      Object.entries(options.headers).forEach(([key, value]) => {
        if (typeof value === 'string') {
          headers[key] = value;
        }
      });
    }
    
    // Add authorization header if token is available
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(url, {
      headers,
      ...options,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Get all checkpoints (legacy method name)
  async getAllCheckpoints(): Promise<Checkpoint[]> {
    const response = await this.request<{data: Checkpoint[]} | Checkpoint[]>(API_ENDPOINTS.CHECKPOINTS);
    return 'data' in response ? response.data : response;
  }

  // Get all checkpoints (new method name)
  async getCheckpoints(): Promise<Checkpoint[]> {
    const response = await this.request<{data: Checkpoint[]} | Checkpoint[]>(API_ENDPOINTS.CHECKPOINTS);
    return 'data' in response ? response.data : response;
  }

  // Get checkpoint by ID
  async getCheckpoint(id: number): Promise<Checkpoint> {
    return this.request<Checkpoint>(API_ENDPOINTS.CHECKPOINT_BY_ID(id));
  }

  // Create new checkpoint
  async createCheckpoint(data: CreateCheckpointData): Promise<Checkpoint> {
    return this.request<Checkpoint>(API_ENDPOINTS.CHECKPOINTS, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Update checkpoint
  async updateCheckpoint(id: number, data: UpdateCheckpointData): Promise<Checkpoint> {
    return this.request<Checkpoint>(API_ENDPOINTS.CHECKPOINT_BY_ID(id), {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Delete checkpoint
  async deleteCheckpoint(id: number): Promise<{ message: string }> {
    return this.request<{ message: string }>(API_ENDPOINTS.CHECKPOINT_BY_ID(id), {
      method: 'DELETE',
    });
  }

  // Toggle checkpoint - Simple (defaults to cascade=true)
  async toggleCheckpoint(id: number): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      API_ENDPOINTS.CHECKPOINT_TOGGLE_WITH_CASCADE(id, true, 'checkpoint_service'), 
      {
        method: 'PUT',
      }
    );
  }

  // Toggle checkpoint - With options (cascade and updated_by)
  async toggleCheckpointWithOptions(id: number, options: ToggleCheckpointOptions): Promise<{ message: string }> {
    const cascade = options.cascade !== undefined ? options.cascade : true;
    return this.request<{ message: string }>(
      API_ENDPOINTS.CHECKPOINT_TOGGLE_WITH_CASCADE(id, cascade, options.updated_by), 
      {
        method: 'PUT',
      }
    );
  }

  // Validation methods for legacy compatibility
  validateCheckpointName(name: string): { isValid: boolean; message?: string } {
    if (!name || name.trim().length < 2) {
      return { isValid: false, message: 'Checkpoint name must be at least 2 characters long' };
    }
    if (name.length > 100) {
      return { isValid: false, message: 'Checkpoint name must be less than 100 characters' };
    }
    return { isValid: true };
  }

  validateDescription(description?: string): { isValid: boolean; message?: string } {
    if (description && description.length > 500) {
      return { isValid: false, message: 'Description must be less than 500 characters' };
    }
    return { isValid: true };
  }
}

// Export singleton instance
export const checkpointService = new CheckpointService();
export default checkpointService;
